import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { notificationsAPI, authAPI } from '../services/api';
import './Settings.css';

const Settings = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Notification settings
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [delivery, setDelivery] = useState('both');
  const [notificationTypes, setNotificationTypes] = useState({
    newStory: true,
    newComment: true,
    commentReaction: true,
    memberJoined: true,
    inviteAccepted: true
  });

  // Profile info
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [bio, setBio] = useState('');
  const { language, setLanguage, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [languageSaved, setLanguageSaved] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Email confirmation
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);

  // Password change
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Account deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Push notifications (mobile only)
  const isNative = !!window.Capacitor?.isNativePlatform();
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pushTypes, setPushTypes] = useState({
    newStory: true,
    newComment: true,
    commentReaction: true,
    memberJoined: true,
    inviteAccepted: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await notificationsAPI.getSettings();
      const s = res.data.settings;
      setNotificationsEnabled(s.notifications ?? true);
      setDelivery(s.notificationDelivery || 'both');
      setNotificationTypes({
        newStory: s.notificationTypes?.newStory ?? true,
        newComment: s.notificationTypes?.newComment ?? true,
        commentReaction: s.notificationTypes?.commentReaction ?? true,
        memberJoined: s.notificationTypes?.memberJoined ?? true,
        inviteAccepted: s.notificationTypes?.inviteAccepted ?? true
      });

      // Load push notification settings
      if (s.pushNotifications !== undefined) {
        setPushEnabled(s.pushNotifications ?? true);
      }
      if (s.pushNotificationTypes) {
        setPushTypes({
          newStory: s.pushNotificationTypes?.newStory ?? true,
          newComment: s.pushNotificationTypes?.newComment ?? true,
          commentReaction: s.pushNotificationTypes?.commentReaction ?? true,
          memberJoined: s.pushNotificationTypes?.memberJoined ?? true,
          inviteAccepted: s.pushNotificationTypes?.inviteAccepted ?? true
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }

    // Load profile
    if (user) {
      setDisplayName(user.displayName || '');
      setFullName(user.fullName || '');
      setBirthday(user.profile?.birthday ? new Date(user.profile.birthday).toISOString().split('T')[0] : '');
      setBirthPlace(user.profile?.birthPlace || '');
      setBio(user.profile?.bio || '');
      // Sync language from user profile to context
      if (user.language) setLanguage(user.language);

      // Load profile photo
      try {
        const photoRes = await authAPI.getProfilePhoto();
        setProfilePhoto(photoRes.data.url);
      } catch (e) {
        // No photo set
      }
    }

    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await notificationsAPI.updateSettings({
        notifications: notificationsEnabled,
        notificationDelivery: delivery,
        notificationTypes,
        pushNotifications: pushEnabled,
        pushNotificationTypes: pushTypes
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileSaved(false);
    try {
      const res = await authAPI.updateMe({
        displayName: displayName.trim(),
        fullName: fullName.trim() || null,
        profile: {
          birthday: birthday || null,
          birthPlace: birthPlace.trim() || null,
          bio: bio.trim() || null
        }
      });
      if (res.data.user) {
        setUser(res.data.user);
      }
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveLanguage = async () => {
    setSavingLanguage(true);
    setLanguageSaved(false);
    try {
      const res = await authAPI.updateMe({ language: selectedLanguage });
      if (res.data.user) {
        setUser(res.data.user);
      }
      setLanguage(selectedLanguage); // Apply globally only after save
      setLanguageSaved(true);
      setTimeout(() => setLanguageSaved(false), 3000);
    } catch (error) {
      console.error('Error saving language:', error);
      alert('Failed to save language');
    } finally {
      setSavingLanguage(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please use JPG, PNG, WebP, or GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo must be under 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await authAPI.uploadProfilePhoto(formData);
      setProfilePhoto(res.data.url);
    } catch (error) {
      console.error('Photo upload error:', error);
      alert('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      await authAPI.deleteProfilePhoto();
      setProfilePhoto(null);
    } catch (error) {
      console.error('Photo remove error:', error);
      alert('Failed to remove photo');
    }
  };

  const toggleType = (type) => {
    setNotificationTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const togglePushType = (type) => {
    setPushTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleResendConfirmation = async () => {
    setResendingConfirmation(true);
    try {
      await authAPI.resendConfirmation();
      setConfirmationSent(true);
      setTimeout(() => setConfirmationSent(false), 5000);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send confirmation email');
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must include at least one uppercase letter');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must include at least one number');
      return;
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) {
      setPasswordError('Password must include at least one special character');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      setPasswordSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');

    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      await authAPI.deleteAccount({});
      logout();
      navigate('/login');
    } catch (error) {
      setDeleteError(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const typeLabels = {
    newStory: { emoji: '🎙️', label: t('newStoryAdded'), description: t('newStoryAddedDesc') },
    newComment: { emoji: '💬', label: t('commentsOnStories'), description: t('commentsOnStoriesDesc') },
    commentReaction: { emoji: '❤️', label: t('reactionsToComments'), description: t('reactionsToCommentsDesc') },
    memberJoined: { emoji: '👋', label: t('newMemberJoined'), description: t('newMemberJoinedDesc') },
    inviteAccepted: { emoji: '🎉', label: t('inviteAccepted'), description: t('inviteAcceptedDesc') }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <header className="settings-header">
          <button className="btn-back" onClick={() => navigate(-1)}>← {t('back')}</button>
          <h1>{t('settingsTitle')}</h1>
        </header>
        <div className="settings-loading">
          <div className="spinner-large"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <header className="settings-header">
        <button className="btn-back" onClick={() => navigate(-1)}>← {t('back')}</button>
        <h1>{t('settingsTitle')}</h1>
      </header>

      <main className="settings-main">
        {/* Quick Links */}
        <div className="settings-quick-links">
          <button className="btn-quick-link" onClick={() => navigate('/')}>🏠 {t('dashboard')}</button>
          <button className="btn-quick-link" onClick={() => navigate('/help')}>❓ {t('helpCenter')}</button>
        </div>

        {/* Profile Section */}
        <section className="settings-section">
          <h2>{t('profile')}</h2>
          <div className="settings-card">
            <div className="account-info">
              <div className="account-avatar-wrapper">
                {profilePhoto ? (
                  <img src={profilePhoto} alt={user?.displayName} className="account-avatar-img" />
                ) : (
                  <div className="account-avatar">
                    {user?.displayName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <label className="photo-upload-btn" title={t('changePhoto')}>
                  📷
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handlePhotoUpload}
                    hidden
                  />
                </label>
                {profilePhoto && (
                  <button className="photo-remove-btn" title={t('removePhoto')} onClick={handlePhotoRemove}>✕</button>
                )}
                {uploadingPhoto && <div className="photo-spinner"><span className="spinner"></span></div>}
              </div>
              <div>
                <p className="account-name">{user?.displayName}</p>
                <p className="account-email">{user?.email}</p>
              </div>
            </div>

            {/* Email Confirmation Status */}
            {user && !user.emailConfirmed && (
              <div className="email-confirm-banner">
                <div className="confirm-info">
                  <span>📧</span>
                  <p>{t('emailNotConfirmed')}</p>
                </div>
                <button
                  className="btn-resend"
                  onClick={handleResendConfirmation}
                  disabled={resendingConfirmation || confirmationSent}
                >
                  {confirmationSent ? `✓ ${t('sent')}` : resendingConfirmation ? t('sending') : t('resend')}
                </button>
              </div>
            )}

            <div className="setting-divider"></div>

            <div className="profile-form">
              <div className="form-group">
                <label>{t('displayName')}</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('displayName')}
                />
              </div>

              <div className="form-group">
                <label>Full Name <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 'normal' }}>— shown on family tree</span></label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. María Elena Rodriguez"
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('birthday')}</label>
                  <input
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('birthplace')}</label>
                  <input
                    type="text"
                    value={birthPlace}
                    onChange={(e) => setBirthPlace(e.target.value)}
                    placeholder={t('birthplacePlaceholder')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('bio')}</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder={t('bioPlaceholder')}
                  rows={2}
                  maxLength={500}
                />
              </div>

              <p className="profile-sync-note">
                💡 {t('profileSyncNote')}
              </p>

              <button
                className="btn-save-profile"
                onClick={handleSaveProfile}
                disabled={savingProfile || !displayName.trim()}
              >
                {savingProfile ? t('saving') : profileSaved ? `✓ ${t('profileSaved')}` : t('saveProfile')}
              </button>
            </div>
          </div>
        </section>

        {/* Language Section */}
        <section className="settings-section">
          <h2>Language / Idioma</h2>
          <div className="settings-card">
            <div className="language-options">
              <button
                className={`language-option ${selectedLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setSelectedLanguage('en')}
              >
                🇺🇸 English
              </button>
              <button
                className={`language-option ${selectedLanguage === 'es' ? 'active' : ''}`}
                onClick={() => setSelectedLanguage('es')}
              >
                🇲🇽 Español
              </button>
            </div>
            <p className="language-note">
              {selectedLanguage === 'es' 
                ? 'El idioma se aplicará a la interfaz de la aplicación.' 
                : 'Language will be applied to the app interface.'}
            </p>
            {selectedLanguage !== (user?.language || 'en') && (
              <button
                className="btn-save-profile"
                onClick={handleSaveLanguage}
                disabled={savingLanguage}
                style={{ marginTop: '12px' }}
              >
                {savingLanguage ? t('saving') : languageSaved ? `✓ ${t('saved') || 'Saved!'}` : t('saveLanguage')}
              </button>
            )}
          </div>
        </section>

        {/* Password Section */}
        <section className="settings-section">
          <h2>{t('security')}</h2>
          <div className="settings-card">
            <div className="setting-row" style={{ cursor: 'pointer' }} onClick={() => setShowPasswordSection(!showPasswordSection)}>
              <div className="setting-info">
                <span className="type-emoji">🔐</span>
                <div>
                  <span className="setting-label">{t('changePassword')}</span>
                  <span className="setting-description">{t('changePasswordDesc')}</span>
                </div>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '18px' }}>
                {showPasswordSection ? '▾' : '▸'}
              </span>
            </div>

            {showPasswordSection && (
              <div className="password-section">
                {passwordError && <div className="inline-error">{passwordError}</div>}
                {passwordSuccess && <div className="inline-success">{passwordSuccess}</div>}
                <div className="form-group">
                  <label>{t('currentPassword')}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('currentPasswordPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('newPassword')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('newPasswordPlaceholder')}
                    minLength={8}
                  />
                  {newPassword.length > 0 && (
                    <ul className="password-rules-inline">
                      {[
                        { label: t('passwordRuleLength'), passed: newPassword.length >= 8 },
                        { label: t('passwordRuleUppercase'), passed: /[A-Z]/.test(newPassword) },
                        { label: t('passwordRuleNumber'), passed: /[0-9]/.test(newPassword) },
                        { label: t('passwordRuleSpecial'), passed: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) },
                      ].map((rule, i) => (
                        <li key={i} style={{ color: rule.passed ? '#22c55e' : 'rgba(255,255,255,0.4)', fontSize: '12px', listStyle: 'none', padding: '2px 0' }}>
                          {rule.passed ? '✓' : '○'} {rule.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('confirmNewPassword')}</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder={t('confirmNewPasswordPlaceholder')}
                  />
                  {confirmNewPassword.length > 0 && newPassword !== confirmNewPassword && (
                    <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{t('passwordsDoNotMatch')}</p>
                  )}
                </div>
                <button
                  className="btn-change-password"
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword}
                >
                  {changingPassword ? t('changingPassword') : t('changePassword')}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Notification Settings */}
        <section className="settings-section">
          <h2>{t('emailNotifications')}</h2>
          <div className="settings-card">
            <div className="setting-row master-toggle">
              <div className="setting-info">
                <span className="setting-label">{t('enableNotifications')}</span>
                <span className="setting-description">{t('enableNotificationsDesc')}</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {notificationsEnabled && (
              <>
                <div className="setting-row delivery-row">
                  <div className="setting-info">
                    <span className="setting-label">{t('deliveryMethod')}</span>
                  </div>
                  <div className="delivery-options">
                    {[
                      { value: 'instant', label: t('instant') },
                      { value: 'digest', label: t('dailyDigest') },
                      { value: 'both', label: t('both') }
                    ].map(option => (
                      <button
                        key={option.value}
                        className={`delivery-option ${delivery === option.value ? 'active' : ''}`}
                        onClick={() => setDelivery(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="setting-divider"></div>

                <div className="setting-row type-header">
                  <span className="setting-label">{t('notificationTypes')}</span>
                </div>

                {Object.entries(typeLabels).map(([key, info]) => (
                  <div key={key} className="setting-row type-row">
                    <div className="setting-info">
                      <span className="type-emoji">{info.emoji}</span>
                      <div>
                        <span className="setting-label">{info.label}</span>
                        <span className="setting-description">{info.description}</span>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationTypes[key]}
                        onChange={() => toggleType(key)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>

        {/* Save Notifications */}
        {/* Push Notifications - Mobile Only */}
        {isNative && (
          <section className="settings-section">
            <h2>📱 Push Notifications</h2>
            <div className="settings-card">
              <div className="setting-row master-toggle">
                <div className="setting-info">
                  <span className="setting-label">Enable push notifications</span>
                  <span className="setting-description">Receive push alerts on this device</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={pushEnabled}
                    onChange={(e) => setPushEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {pushEnabled && (
                <>
                  <div className="setting-divider"></div>

                  <div className="setting-row type-header">
                    <span className="setting-label">Push alert types</span>
                  </div>

                  {Object.entries(typeLabels).map(([key, info]) => (
                    <div key={key} className="setting-row type-row">
                      <div className="setting-info">
                        <span className="type-emoji">{info.emoji}</span>
                        <div>
                          <span className="setting-label">{info.label}</span>
                          <span className="setting-description">{info.description}</span>
                        </div>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={pushTypes[key]}
                          onChange={() => togglePushType(key)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </>
              )}
            </div>
          </section>
        )}

        <div className="settings-save">
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('saving') : saved ? `✓ ${t('saved')}` : t('saveChanges')}
          </button>
        </div>

        {/* Billing & Subscription */}
        <section className="settings-section">
          <h2>💳 {t('settingsSubscription')}</h2>
          <div className="settings-card">
            <div className="setting-row">
              <div className="setting-info">
                <span className="type-emoji">
                  {user?.subscription?.tier === 'legacy_keeper' ? '🌳' :
                   user?.subscription?.tier === 'legacy_vault' ? '🏛️' : '🌱'}
                </span>
                <div>
                  <span className="setting-label">
                    {user?.subscription?.tier === 'legacy_keeper' ? 'Legacy Keeper' :
                     user?.subscription?.tier === 'legacy_vault' ? 'Legacy Vault' :
                     t('settingsFreePlan')}
                  </span>
                  <span className="setting-description">
                    {!user?.subscription?.tier || user?.subscription?.tier === 'free'
                      ? t('settingsUpgradeDesc')
                      : user?.subscription?.interval === 'annual' ? t('settingsAnnualBilling') : t('settingsMonthlyBilling')
                    }
                  </span>
                </div>
              </div>
              <button
                className="btn-manage-plan"
                onClick={() => navigate('/pricing')}
              >
                {!user?.subscription?.tier || user?.subscription?.tier === 'free' ? t('pricingUpgrade') : t('settingsManagePlan')}
              </button>
            </div>
          </div>
        </section>

<div style={{
  textAlign: 'center', marginTop: '24px', padding: '14px',
  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '12px'
}}>
<span style={{ fontSize: '16px', marginRight: '4px', filter: 'none', opacity: 1 }}>🎁</span>
<span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
  Have a gift code?{' '}
  <span onClick={() => navigate('/redeem')} style={{ color: '#a78bfa', cursor: 'pointer', fontWeight: '500' }}>
    Redeem here
  </span>
</span>
</div>

        {/* Danger Zone */}
        <section className="settings-section danger-section">
          <h2>{t('dangerZone')}</h2>
          <div className="settings-card danger-card">
            <div className="setting-row">
              <div className="setting-info">
                <span className="type-emoji">⚠️</span>
                <div>
                  <span className="setting-label">{t('deleteAccount')}</span>
                  <span className="setting-description">{t('deleteAccountDesc')}</span>
                </div>
              </div>
              <button
                className="btn-delete-account"
                onClick={() => setShowDeleteModal(true)}
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </section>

        <div className="settings-version">
          EchoMemoir v1.3.4
        </div>
      </main>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>⚠️ {t('deleteAccount')}</h2>
            <p className="modal-description">
              {t('deleteAccountWarning')}
            </p>

            {deleteError && <div className="auth-error" style={{ marginBottom: '16px' }}>{deleteError}</div>}

            <div className="form-group">
              <label>{t('typeDelete')}</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '15px',
                  outline: 'none'
                }}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); setDeleteError(''); }}
                disabled={deleting}
              >
                {t('cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
              >
                {deleting ? t('deletingAccount') : t('deleteMyAccount')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;